import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { API, toast } from '@/App';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '💻',
    features: [],
    order: 0,
    active: true,
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, formData);
        toast.success('Hizmet güncellendi!');
      } else {
        await axios.post(`${API}/services`, formData);
        toast.success('Hizmet eklendi!');
      }
      setDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error('Bir hata oluştu!');
      console.error('Error saving service:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hizmeti silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success('Hizmet silindi!');
      fetchServices();
    } catch (error) {
      toast.error('Silme başarısız!');
      console.error('Error deleting service:', error);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      icon: service.icon,
      features: service.features || [],
      order: service.order,
      active: service.active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      title: '',
      description: '',
      icon: '💻',
      features: [],
      order: 0,
      active: true,
    });
    setFeatureInput('');
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({ ...formData, features: [...formData.features, featureInput.trim()] });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-services-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Hizmetler</h1>
            <p className="text-white/60">Sunduğunuz hizmetleri yönetin</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => resetForm()}
                data-testid="add-service-button"
                className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Hizmet
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1f] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Başlık</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="service-title-input"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Açıklama</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={3}
                    data-testid="service-description-input"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Icon (Emoji)</label>
                  <Input
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    required
                    data-testid="service-icon-input"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Özellikler</label>
                  <div className="flex space-x-2 mb-2">
                    <Input
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      placeholder="Özellik ekle"
                      data-testid="service-feature-input"
                      className="bg-white/5 border-white/10 text-white flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    />
                    <Button type="button" onClick={addFeature} data-testid="add-feature-button">
                      Ekle
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center justify-between bg-white/5 p-2 rounded">
                        <span className="text-sm">{feature}</span>
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Sıra</label>
                    <Input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      data-testid="service-order-input"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Aktif</label>
                    <select
                      value={formData.active.toString()}
                      onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                      data-testid="service-active-select"
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                    >
                      <option value="true">Evet</option>
                      <option value="false">Hayır</option>
                    </select>
                  </div>
                </div>
                <Button type="submit" data-testid="service-submit-button" className="w-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
                  {editingService ? 'Güncelle' : 'Ekle'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-white/60">Yükleniyor...</div>
        ) : services.length === 0 ? (
          <div className="glass p-8 rounded-2xl text-center text-white/60" data-testid="no-services-message">
            Henüz hizmet eklenmemiş. Hemen bir tane ekleyin!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div key={service.id} className="glass p-6 rounded-2xl" data-testid={`service-item-${service.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div className="text-4xl">{service.icon}</div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(service)}
                      data-testid={`edit-service-${service.id}`}
                      className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      data-testid={`delete-service-${service.id}`}
                      className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-sm text-white/60 mb-3">{service.description}</p>
                {service.features && service.features.length > 0 && (
                  <div className="text-xs text-white/50">{service.features.length} özellik</div>
                )}
                <div className="mt-3 flex items-center justify-between text-xs text-white/50">
                  <span>Sıra: {service.order}</span>
                  <span className={service.active ? 'text-green-500' : 'text-red-500'}>
                    {service.active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminServices;